<script lang="ts">
  /**
   * The Home tab: the New game tile, always in the same place, then the
   * game's own sections in the order it gives them, each under its label
   * ("Continue a game"; Interlock's "Today" and "Your bests"). TwinTen's
   * `HomeTab` layout.
   *
   * - `invites` (since 1.2.0): "Invites for you", at the very top and only
   *   when there is one — somebody waiting on this player comes before
   *   anything the player might start. Each invite is a compact
   *   `InviteCard` (who, for what, Join, Decline); past `limit` (3) a
   *   "+N more" reveals the rest, so one invite never pushes New game off a
   *   phone's first screen. The game decides who sees invites at all (not a
   *   guest, not offline) by what it passes.
   * - `newGame`: the one filled tile (`NewGameTile`, marked as the screen's
   *   primary action).
   * - `install`: a one-time card under it (an `InstallCard`), when the game
   *   has one to show.
   * - `sections`: each a label and either its `content` (the game's cards
   *   or rows, stacked at the section's gap) or, without content, its
   *   `empty` sentence. Zero game cards is a normal Home: a game with no
   *   saved games simply has no such section, or an empty one.
   * - `aside`: a column of its own from 1024px (TwinTen's "Friends online"),
   *   under the sections on a phone.
   *
   * From 1024px: New game on the left and the sections on the right; with
   * an `aside`, New game over the sections on the left and the aside on the
   * right (the sections start right under the tile however tall the aside
   * is). Invites sit over New game, in its column.
   *
   * Nothing here fetches. Test ids: `testid` on the page (default
   * `ui-home`), `newGame.testid` on the tile, each section's `testid` on its
   * `<section>` and `emptyTestid` on its empty sentence; `invites.testid`
   * (default `ui-home-invites`) on the invites' section, each invite's
   * `testid` on its card and its answers' `testid`s on the buttons,
   * `invites.moreTestid` (default `ui-home-invites-more`) on "+N more".
   */
  import { tick, type Snippet } from 'svelte';

  import EmptyState from '../components/EmptyState.svelte';
  import InviteCard from '../components/InviteCard.svelte';
  import NewGameTile from '../components/NewGameTile.svelte';
  import SectionLabel from '../components/SectionLabel.svelte';
  import { shellText } from '../i18n.svelte.js';
  import {
    HOME_INVITES_SHOWN,
    type HomeInvites,
    type HomeNewGame,
    type HomeSection,
  } from './menuTypes.js';

  interface Props {
    readonly newGame: HomeNewGame;
    readonly sections: readonly HomeSection[];
    readonly invites?: HomeInvites | undefined;
    readonly install?: Snippet | undefined;
    readonly aside?: Snippet | undefined;
    readonly testid?: string | undefined;
  }
  const { newGame, sections, invites, install, aside, testid = 'ui-home' }: Props = $props();

  const uid = $props.id();
  const invitesLabelId = `${uid}-invites`;
  const invitesListId = `${uid}-invites-list`;

  /**
   * "+N more" pressed: every invite shows — until the list is back within
   * the limit, after which invites that arrive later are folded again
   * rather than all drawn.
   */
  let revealed = $state(false);
  let invitesList = $state<HTMLUListElement | undefined>();
  let page = $state<HTMLDivElement | undefined>();

  const inviteItems = $derived(invites?.items ?? []);
  const limit = $derived(Math.max(1, Math.floor(invites?.limit ?? HOME_INVITES_SHOWN)));
  const shownInvites = $derived(revealed ? inviteItems : inviteItems.slice(0, limit));
  const hiddenInvites = $derived(inviteItems.length - shownInvites.length);
  const moreWords = $derived(
    invites?.formatMore?.(hiddenInvites) ?? shellText('invitesMore', { count: hiddenInvites }),
  );

  $effect(() => {
    if (inviteItems.length <= limit) revealed = false;
  });

  /** Shows the rest, and puts focus on the first one it revealed (the button itself goes). */
  async function revealInvites(): Promise<void> {
    const first = shownInvites.length;
    revealed = true;
    await tick();
    invitesList?.children[first]?.querySelector<HTMLButtonElement>('button')?.focus();
  }

  /**
   * Decline, and keep the keyboard's place: when the game takes the card
   * away (at once, or by the next render), focus would fall to the page.
   * It goes to the invite now in that place instead — or, with none left,
   * New game.
   */
  async function decline(index: number, answer: () => void): Promise<void> {
    answer();
    await tick();
    const active = document.activeElement;
    if (active !== null && active !== document.body && page?.contains(active) === true) return;
    const cards = invitesList?.children;
    const next =
      cards === undefined || cards.length === 0
        ? undefined
        : cards[Math.min(index, cards.length - 1)];
    const target =
      next?.querySelector<HTMLButtonElement>('button') ??
      page?.querySelector<HTMLElement>('[data-shell-primary]');
    target?.focus();
  }
</script>

<div
  class="ui-home"
  class:ui-home--aside={aside !== undefined}
  data-testid={testid}
  bind:this={page}
>
  <div class="ui-home-start">
    {#if invites !== undefined && inviteItems.length > 0}
      <section
        class="ui-home-invites"
        aria-labelledby={invitesLabelId}
        data-testid={invites.testid ?? 'ui-home-invites'}
      >
        <SectionLabel as="h2" id={invitesLabelId}>{invites.label}</SectionLabel>
        <!-- role="list": a list styled without markers stays a list to VoiceOver. -->
        <ul class="ui-home-invite-list" role="list" id={invitesListId} bind:this={invitesList}>
          {#each shownInvites as invite, index (invite.id)}
            <li>
              <InviteCard
                compact
                initial={invite.initial}
                title={invite.name}
                detail={invite.detail}
                primaryLabel={invite.join.label}
                primaryName={invite.join.name}
                onprimary={invite.join.onclick}
                busy={invite.join.busy === true}
                primaryTestid={invite.join.testid}
                secondaryLabel={invite.decline.label}
                secondaryName={invite.decline.name}
                onsecondary={() => void decline(index, invite.decline.onclick)}
                secondaryTestid={invite.decline.testid}
                testid={invite.testid}
              />
            </li>
          {/each}
        </ul>
        {#if hiddenInvites > 0}
          <button
            type="button"
            class="ui-home-more"
            aria-controls={invitesListId}
            data-testid={invites.moreTestid ?? 'ui-home-invites-more'}
            onclick={() => void revealInvites()}
          >
            {moreWords}
          </button>
        {/if}
      </section>
    {/if}

    <NewGameTile
      title={newGame.title}
      detail={newGame.detail}
      icon={newGame.icon}
      primary
      testid={newGame.testid}
      onclick={newGame.onclick}
    />
    {@render install?.()}
  </div>

  {#if sections.length > 0}
    <div class="ui-home-sections">
      {#each sections as section (section.id)}
        {@const labelId = `${uid}-${section.id}`}
        <section class="ui-home-section" aria-labelledby={labelId} data-testid={section.testid}>
          <SectionLabel as="h2" id={labelId}>{section.label}</SectionLabel>
          {#if section.content !== undefined}
            <div class="ui-home-list">{@render section.content()}</div>
          {:else if section.empty !== undefined}
            <EmptyState text={section.empty} testid={section.emptyTestid} />
          {/if}
        </section>
      {/each}
    </div>
  {/if}

  {#if aside !== undefined}
    <div class="ui-home-aside">{@render aside()}</div>
  {/if}
</div>

<style>
  .ui-home {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-4);
    padding: var(--ui-space-2) var(--ui-gutter) var(--ui-space-5);
    font-family: var(--ui-font);
  }

  .ui-home-start {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
  }

  .ui-home-sections {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-4);
  }

  .ui-home-section,
  .ui-home-invites {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }

  /* Over New game, a section's distance from it (the start column's own gap
     is the tile's to its install card). */
  .ui-home-invites {
    margin-bottom: var(--ui-space-1);
  }

  .ui-home-list {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  /* Beside the tablet rail (1.6.0, `AppFrame`'s `rail`): a section of full
     game cards puts them two to a row, so the column's width is used; a list
     of compact cards (one slim row each) and any other section keep the
     phone's list. */
  :global(.ui-frame--rail) .ui-home-list:has(:global(.ui-game-card:not(.ui-game-card--compact))) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* The same in the frame's first frame (1.8.0, `auto`), by the rail's range. */
  @media (min-width: 600px) and (max-width: 1023.98px) {
    :global(.ui-frame--rail-auto)
      .ui-home-list:has(:global(.ui-game-card:not(.ui-game-card--compact))) {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .ui-home-invite-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }

  /* "+N more": a word, not a shape, so it never reads as a second New game. */
  .ui-home-more {
    align-self: flex-start;
    min-height: 2.75rem;
    padding: 0 0.375rem;
    border: 0;
    border-radius: var(--ui-radius-sm);
    background: transparent;
    color: var(--ui-accent);
    font: var(--ui-text-label);
    font-weight: 800;
  }

  .ui-home-more:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  /* From 1024px: New game (and the install card) on the left, the sections
     on the right — the width used, not one long column in a wide window. */
  @media (min-width: 1024px) {
    .ui-home {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
      column-gap: var(--ui-space-6);
      align-items: start;
      padding: var(--ui-space-5) var(--ui-gutter-wide) var(--ui-space-6);
    }

    /* With an aside: New game over the sections on the left, the aside on
       the right. The aside spans both rows; when it is the taller column,
       the height it adds goes to the sections' row (the flexible one), never
       to New game's, so no gap opens under the tile (1.8.0). */
    .ui-home--aside {
      grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
      grid-template-rows: auto 1fr;
      row-gap: var(--ui-space-4);
    }

    .ui-home--aside .ui-home-start {
      grid-column: 1;
      grid-row: 1;
    }

    .ui-home--aside .ui-home-sections {
      grid-column: 1;
      grid-row: 2;
    }

    .ui-home--aside .ui-home-aside {
      grid-column: 2;
      grid-row: 1 / span 2;
    }
  }
</style>
